/**
 * Revenue — the SUPERUSER-only revenue & usage dashboard.
 *
 * Reachable only at /revenue and only when /auth/me reports is_superuser (the
 * route in App.jsx redirects everyone else home), so a normal member never sees
 * it and nothing here touches another user's data. It reads two owner-scoped,
 * superuser-gated endpoints — /billing/revenue-summary/ (the same numbers as the
 * partner feed: subscribers, MRR, potential, collected/fees/net/payout, the
 * editable entries, usage and the recurring lines) and /budget/months/ensure/
 * (the owner's own month, to show Vault's expenses + incomes) — and offers full
 * CRUD over the editable RevenueEntry rows: add a recurring product (projected),
 * an invoice/one-off added to income, or a manual adjustment. Everything the
 * partner feed reports is shown here so the owner sees, in one place, what's
 * coming in (current vs potential), what's recurring, and what it costs.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api.js';
// Vault's own prices and revenue are rand (Paystack), whatever the account's currency.
import { fmtZAR as fmtR } from '../money.js';

/* 'YYYY-MM' helpers (local, no deps). */
const mkKey = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const shiftKey = (key, n) => {
  const [y, m] = key.split('-').map(Number);
  return mkKey(new Date(y, m - 1 + n, 1));
};
const monthLabel = (key) => {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
};

const BLANK = { label: '', amount_zar: '', type: 'manual', note: '' };
const TYPES = [
  ['projected', 'Recurring / product (coming)'],
  ['one_off', 'Invoice / one-off (received)'],
  ['manual', 'Manual income (received)'],
  ['adjustment', 'Adjustment (+/−)'],
];

export default function Revenue() {
  const [month, setMonth] = useState(mkKey());
  const [sum, setSum] = useState(null);
  const [budget, setBudget] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);   // {id?, label, amount_zar, type, note}
  const [users, setUsers] = useState(null);   // superuser user manager (test accounts)
  const [uYearly, setUYearly] = useState(false);   // yearly plan configured?
  const [uErr, setUErr] = useState('');
  const [tab, setTab] = useState('income');   // income | expenses | users | testers
  const [testers, setTesters] = useState(null);   // superuser tester manager
  const [tSel, setTSel] = useState([]);            // selected tester ids
  const [tMsg, setTMsg] = useState({ subject: 'Vault', body: '', channel: 'email' });
  const [tInfo, setTInfo] = useState('');
  const [tList, setTList] = useState(null);        // owner's tester email list + account states
  const [tPaste, setTPaste] = useState('');        // pasted emails to add
  const [tListInfo, setTListInfo] = useState('');

  const load = useCallback(async () => {
    setErr('');
    try {
      const [s, b] = await Promise.all([
        api(`/billing/revenue-summary/?month=${month}`),
        api(`/budget/months/ensure/?month=${month}`),
      ]);
      setSum(s);
      setBudget(b);
    } catch (e) {
      setErr(e.message || 'Could not load revenue.');
    }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  // ── Superuser user manager: list accounts, comp (unbill) or delete them. ──
  const loadUsers = useCallback(async () => {
    try { const d = await api('/auth/admin/users/'); setUsers(d.users || []); setUYearly(!!d.yearly_available); setUErr(''); }
    catch (e) { setUErr(e.data?.detail || e.message); }
  }, []);
  useEffect(() => { loadUsers(); }, [loadUsers]);
  const loadTesters = useCallback(async () => {
    try { const d = await api('/auth/admin/testers/'); setTesters(d.testers || []); }
    catch (e) { setUErr(e.data?.detail || e.message); }
  }, []);
  useEffect(() => { loadTesters(); }, [loadTesters]);
  const loadTesterList = useCallback(async () => {
    try { setTList(await api('/auth/admin/testers/emails/')); }
    catch (e) { setTListInfo(e.data?.detail || e.message); }
  }, []);
  useEffect(() => { loadTesterList(); }, [loadTesterList]);
  // Land on the SAME active month the dashboard shows (payday-aware), not just
  // the calendar month, so Vault's own incomes/expenses match the app.
  useEffect(() => {
    let alive = true;
    api('/budget/months/current/').then((d) => { if (alive && d?.month) setMonth(d.month); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  // Put a user in Testing (free until go-live), Live (billed) or Comped (free
  // for good). Going free cancels any live Paystack subscription server-side;
  // Testing/Comped → Live asks the user to confirm on their next open.
  const setPhase = async (u, phase) => {
    if (u.billing_phase === phase) return;
    try {
      await api(`/auth/admin/users/${u.id}/`, { method: 'PATCH', body: { billing_phase: phase } });
      await Promise.all([loadUsers(), loadTesterList()]);
    } catch (e) { setUErr(e.data?.detail || e.message); }
  };

  // Tester email list: add pasted emails (matching accounts move to Testing).
  const addTesterEmails = async () => {
    if (!tPaste.trim()) { setTListInfo('Paste one or more email addresses first.'); return; }
    try {
      const r = await api('/auth/admin/testers/emails/', { method: 'POST', body: { emails: tPaste } });
      setTListInfo(`Found ${r.found}, added ${r.added} new, moved ${r.moved_to_testing.length} account(s) to Testing.`);
      setTPaste('');
      await Promise.all([loadTesterList(), loadUsers()]);
    } catch (e) { setTListInfo(e.data?.detail || e.message); }
  };
  const removeTesterEmail = async (email) => {
    try {
      await api('/auth/admin/testers/emails/', { method: 'DELETE', body: { email } });
      await loadTesterList();
    } catch (e) { setTListInfo(e.data?.detail || e.message); }
  };
  // Bulk: every account in Testing → Live (each confirms on next open, nothing
  // charged until they accept and pay) or → Comped (free for good, never asked).
  const bulkTesters = async (phase) => {
    const n = tList?.testing_count || 0;
    const msg = phase === 'live'
      ? `Move all ${n} testing account(s) to Live? Each one is asked to accept and pay, or stop, the next time they open Vault. Nothing is charged until they accept.`
      : `Move all ${n} testing account(s) to Comped? They stay free for good and are never asked.`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    try {
      const r = await api('/auth/admin/testers/phase/', { method: 'POST', body: { phase } });
      setTListInfo(`Moved ${r.count} account(s) to ${phase === 'live' ? 'Live' : 'Comped'}.`);
      await Promise.all([loadTesterList(), loadUsers()]);
    } catch (e) { setTListInfo(e.data?.detail || e.message); }
  };

  // Set a user's plan interval (monthly/yearly). For an ACTIVE subscriber the
  // server switches the Paystack plan with no double charge; otherwise it just
  // records the preference for when billing starts.
  const setUserInterval = async (u, interval) => {
    if ((u.billing_interval || 'monthly') === interval) return;
    try { await api(`/auth/admin/users/${u.id}/`, { method: 'PATCH', body: { billing_interval: interval } }); await loadUsers(); }
    catch (e) { setUErr(e.data?.detail || e.message); }
  };

  // Enable (resume or start) or disable (cancel) a user's subscription as owner.
  const setUserSub = async (u, action) => {
    try { await api(`/auth/admin/users/${u.id}/`, { method: 'PATCH', body: { action } }); await loadUsers(); }
    catch (e) { setUErr(e.data?.detail || e.message); }
  };
  const delUser = async (u) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${u.email}? This permanently removes the account and all its data.`)) return;
    try { await api(`/auth/admin/users/${u.id}/`, { method: 'DELETE' }); await loadUsers(); }
    catch (e) { setUErr(e.data?.detail || e.message); }
  };
  const toggleSel = (id) => setTSel((v) => (v.includes(id) ? v.filter((x) => x !== id) : [...v, id]));
  const sendToTesters = async () => {
    if (!tMsg.body.trim()) { setTInfo('Write a message first.'); return; }
    if (!tSel.length) { setTInfo('Select at least one tester.'); return; }
    try {
      const r = await api('/auth/admin/testers/message/', { method: 'POST',
        body: { ids: tSel, channel: tMsg.channel, subject: tMsg.subject, body: tMsg.body } });
      setTInfo(`Sent ${r.sent}, failed ${r.failed}.`);
      await loadTesters();
    } catch (e) { setTInfo(e.data?.detail || e.message); }
  };

  /** Confirm an Android tester's email is on the Google Play tester list: the
   * server grants their free months once and emails them the join link. */
  const markPlayAdded = async (t) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Is ${t.email} on the Play Console tester list now? This grants their free months and emails them the join link.`)) return;
    try {
      const r = await api(`/auth/admin/testers/${t.id}/play-added/`, { method: 'POST' });
      const what = {
        granted: 'free months added to their account',
        on_signup: 'free months added when they sign up with that email',
        paying: 'they are already paying, so nothing was changed',
        already_free: 'their account is already free, so nothing was changed',
        golive_pending: 'they still have to answer the go-live screen, so nothing was changed (decide on Users)',
      }[r.outcome] || (r.already ? 'already marked before' : 'done');
      setTInfo(`${t.email}: ${what}${r.already ? '' : r.emailed ? '; join link emailed.' : '; the join-link email FAILED, send it by hand.'}`);
      await loadTesters();
    } catch (e) { setTInfo(e.data?.detail || e.message); }
  };

  async function saveDraft() {
    if (!draft?.label?.trim()) { setErr('Give the entry a label.'); return; }
    setBusy(true);
    try {
      const body = {
        month,
        label: draft.label.trim(),
        amount_zar: Number(draft.amount_zar) || 0,
        type: draft.type,
        note: draft.note || '',
      };
      if (draft.id) await api(`/billing/revenue-entries/${draft.id}/`, { method: 'PATCH', body });
      else await api('/billing/revenue-entries/', { method: 'POST', body });
      setDraft(null);
      await load();
    } catch (e) {
      setErr(e.message || 'Could not save the entry.');
    } finally { setBusy(false); }
  }

  async function delEntry(id) {
    setBusy(true);
    try { await api(`/billing/revenue-entries/${id}/`, { method: 'DELETE' }); await load(); }
    catch (e) { setErr(e.message || 'Could not delete.'); }
    finally { setBusy(false); }
  }

  // Tick an invoice/product as received (money landed) → it becomes realised
  // income instead of "to come".
  async function toggleReceived(e) {
    setBusy(true);
    try { await api(`/billing/revenue-entries/${e.id}/`, { method: 'PATCH', body: { received: !e.received } }); await load(); }
    catch (x) { setErr(x.message || 'Could not update.'); }
    finally { setBusy(false); }
  }

  const items = budget?.items || [];
  const incomes = useMemo(() => items.filter((i) => i.kind === 'income'), [items]);
  const expenses = useMemo(() => items.filter((i) => i.kind === 'expense'), [items]);

  const s = sum;

  return (
    <div className="min-h-full bg-ink text-slate-100">
      <div className="w-full max-w-screen-2xl mx-auto px-4 md:px-8 py-6">
        {/* Header + month nav */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold">Revenue &amp; usage</h1>
            <p className="text-xs text-slate-500">Superuser only · Vault is the source of truth for its income</p>
          </div>
          <a href="/app" className="text-xs text-vblue">← App</a>
        </div>
        <div className="flex items-center justify-center gap-3 mb-4">
          <button className="w-8 h-8 rounded-full border border-edge" onClick={() => setMonth((k) => shiftKey(k, -1))}>‹</button>
          <span className="text-sm font-semibold min-w-[10rem] text-center">{monthLabel(month)}</span>
          <button className="w-8 h-8 rounded-full border border-edge" onClick={() => setMonth((k) => shiftKey(k, 1))}>›</button>
        </div>

        {err && <div className="mb-3 text-sm text-red-400 border border-red-400/40 rounded-lg px-3 py-2">{err}</div>}

        <AISwitchCard />

        {/* Tabs — Income · Expenses · Users, so each is clear on its own. */}
        <div className="flex gap-2 mb-4">
          {[['income', 'Income'], ['expenses', 'Expenses'], ['users', 'Users'], ['testers', 'Testers']].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
                    className={`flex-1 py-2 rounded-xl border text-sm transition ${
                      tab === k ? 'border-vblue bg-vblue/10 text-vblue font-semibold'
                                : 'border-edge text-slate-400 hover:border-vblue/50'}`}>
              {l}
            </button>
          ))}
        </div>

        {tab === 'income' && (!s ? (
          <p className="text-slate-500 text-sm">Loading…</p>
        ) : (
          <>
            {/* Headline income now vs potential */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <Hero label="Income now" value={s.current_zar} sub={`as of ${new Date(s.as_of).toLocaleString('en-ZA')}`} accent="emerald" />
              <Hero label="Potential" value={s.potential_zar} sub={`${s.subscribers.active} active · ${s.subscribers.trialing} trialing`} accent="blue" />
            </div>

            {/* Money grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
              <Stat label="Total income" value={s.total_income_zar} />
              <Stat label="MRR" value={s.mrr_zar} />
              <Stat label="Collected" value={s.collected_zar} />
              <Stat label="Net" value={s.net_zar} />
              <Stat label={`Payout · ${s.payout_bank}`} value={s.payout_zar} />
              <Stat label="Adjustments" value={s.adjustments_zar} />
            </div>

            {/* Subscribers & billing — who's being billed, who's in/out of
                trial, and the monthly cost per user (Vault's own accounts). */}
            <Section title="Subscribers &amp; billing">
              <div className="text-[11px] text-slate-400 mb-2 leading-relaxed">
                {fmtR(s.price_zar)}/user · month — <span className="text-emerald-400">{s.subscribers.active} billed now</span>,{' '}
                {s.subscribers.trialing} trial{s.subscribers.trialing === 1 ? '' : 's'} converting →{' '}
                <span className="text-vblue font-semibold">{s.subscribers.active + s.subscribers.trialing} monthly payers</span>{' '}
                = {fmtR((s.subscribers.active + s.subscribers.trialing) * s.price_zar)} potential MRR
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                <Pill k="Registered" v={s.subscribers.registered} />
                <Pill k="Active (billed)" v={s.subscribers.active} />
                <Pill k="In trial · card" v={s.subscribers.trialing} />
                <Pill k="Trial · no card" v={s.subscribers.trialing_no_card} />
                <Pill k="Trial lapsed" v={s.subscribers.trial_lapsed} />
                <Pill k="Past due" v={s.subscribers.past_due} />
                <Pill k="Cancelled (yr)" v={s.subscribers.cancelled_mtd} />
              </div>
            </Section>

            {/* Recurring / coming */}
            <Section title="Recurring &amp; invoices coming">
              {(!s.recurring || s.recurring.length === 0) ? (
                <p className="text-xs text-slate-500">Nothing recurring yet — add a product or invoice below.</p>
              ) : s.recurring.map((r, i) => (
                <div key={`${r.label}-${i}`} className="flex items-center justify-between py-1.5 border-b border-edge last:border-0">
                  <div>
                    <div className="text-sm">{r.label}</div>
                    <div className="text-[10px] text-slate-500">{r.interval} · next {r.next}</div>
                  </div>
                  <div className="text-sm font-semibold text-emerald-400">{fmtR(r.amount_zar)}</div>
                </div>
              ))}
            </Section>

            {/* Editable revenue entries */}
            <Section title="Set revenue in Vault" action={
              <button className="text-xs text-vblue" onClick={() => setDraft({ ...BLANK })}>+ Add entry</button>
            }>
              {draft && (
                <div className="border border-edge rounded-xl p-3 mb-3 grid gap-2 bg-black/20">
                  <input className="input text-sm" placeholder="Label — e.g. Annual plan top-up, Client invoice #12"
                         value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
                  <div className="grid grid-cols-2 gap-2">
                    <input className="input text-sm" type="number" placeholder="Amount (R)"
                           value={draft.amount_zar} onChange={(e) => setDraft({ ...draft, amount_zar: e.target.value })} />
                    <select className="input text-sm" value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })}>
                      {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <input className="input text-sm" placeholder="Note (optional)"
                         value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
                  <div className="flex gap-2">
                    <button className="btn-primary text-sm flex-1" disabled={busy} onClick={saveDraft}>{draft.id ? 'Save' : 'Add'}</button>
                    <button className="text-sm px-3 border border-edge rounded-lg" onClick={() => setDraft(null)}>Cancel</button>
                  </div>
                </div>
              )}
              {(!s.entries || s.entries.length === 0) ? (
                <p className="text-xs text-slate-500">No hand-set entries this month.</p>
              ) : s.entries.map((e) => (
                <div key={e.id} className="flex items-center justify-between py-1.5 border-b border-edge last:border-0">
                  <div className="min-w-0">
                    <div className="text-sm truncate">
                      {e.label}
                      {(e.type === 'projected' || e.type === 'one_off') && (
                        <span className={`ml-1.5 text-[9px] px-1.5 py-0.5 rounded ${e.received ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}>
                          {e.received ? 'received' : 'to come'}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-500">{(TYPES.find((t) => t[0] === e.type) || [, e.type])[1]}{e.note ? ` · ${e.note}` : ''}{e.external_ref ? ' · synced' : ''}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-sm font-semibold ${Number(e.amount_zar) < 0 ? 'text-red-400' : 'text-emerald-400'}`}>{fmtR(e.amount_zar)}</span>
                    {(e.type === 'projected' || e.type === 'one_off') && (
                      <button className="text-[11px] text-emerald-400" onClick={() => toggleReceived(e)}
                              title={e.received ? 'Mark as not received' : 'Mark received (money landed)'}>
                        {e.received ? 'untick' : 'tick ✓'}
                      </button>
                    )}
                    <button className="text-[11px] text-slate-400" onClick={() => setDraft({ id: e.id, label: e.label, amount_zar: e.amount_zar, type: e.type, note: e.note || '' })}>edit</button>
                    <button className="text-[11px] text-red-400" onClick={() => delEntry(e.id)}>✕</button>
                  </div>
                </div>
              ))}
            </Section>

            {/* The owner's own budget — expenses + incomes for the month */}
          </>
        ))}

        {tab === 'expenses' && (!s ? (
          <p className="text-slate-500 text-sm">Loading…</p>
        ) : (
          <>
            <Section title="Vault's running costs">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <Stat label="Paystack fees" value={s.fees_zar} neg />
                <Stat label="AI (Gemini/Claude)" value={s.usage?.ai_zar} neg />
                <Stat label="Infra" value={s.usage?.infra_zar} neg />
              </div>
              <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
                Vault's cost of doing business this month — payment fees on collected income,
                AI usage, and infrastructure.
              </p>
            </Section>
          </>
        ))}

        {tab === 'users' && (
        <Section title="Users — testing / live / comped, or delete (superuser)">
          {uErr && <div className="text-xs text-red-400 mb-2">{uErr}</div>}
          {users && (() => {
            const price = Number(s?.price_zar || 0) || 59;
            const paying = users.filter((u) => !u.is_superuser && u.billing_phase === 'live' && u.subscription_status === 'active');
            return (
              <div className="text-[11px] text-slate-400 mb-2 leading-relaxed">
                <span className="text-emerald-400 font-semibold">{paying.length} paying</span> · {fmtR(price)}/user
                = <span className="text-vblue font-semibold">{fmtR(paying.length * price)}/mo from users</span>
                {' · '}{users.length} total account{users.length === 1 ? '' : 's'}
              </div>
            );
          })()}
          <div className="text-[11px] text-slate-500 mb-3 leading-relaxed">
            <span className="text-amber-300 font-semibold">Testing</span> is free and never charged, until you move them to Live.{' '}
            <span className="text-vblue font-semibold">Live</span> is the real subscription at {fmtR(Number(s?.price_zar || 0) || 59)}/mo; anyone moved here from Testing or Comped must accept and pay, or stop, the next time they open Vault.{' '}
            <span className="text-emerald-400 font-semibold">Comped</span> is free for good and never asked. Going free cancels any live subscription.
          </div>
          {!users ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : users.length === 0 ? (
            <div className="text-sm text-slate-500">No users yet.</div>
          ) : (
            <div className="grid gap-1">
              {users.map((u) => (
                <div key={u.id} className="flex flex-wrap items-center gap-2 border-b border-edge last:border-0 py-2">
                  <div className="min-w-[180px] flex-1">
                    <div className="text-sm truncate">{u.email}{u.is_superuser ? ' · owner' : ''}</div>
                    <div className="text-[10px] text-slate-500">
                      {u.is_superuser ? 'owner · full access'
                        : u.billing_phase === 'testing' ? 'testing · free until go-live'
                        : u.billing_phase === 'comped' ? 'comped · free for good, R0'
                        : u.golive_pending ? 'live · waiting for them to accept or decline'
                        : u.subscription_status === 'active'
                          ? `billed · ${u.billing_interval === 'yearly' ? 'yearly' : fmtR(Number(s?.price_zar || 0) || 59) + '/mo'} · active`
                          : `billed · ${u.billing_interval === 'yearly' ? 'yearly' : 'monthly'} · ${u.subscription_status}`}
                      {u.golive_response ? ` · go-live ${u.golive_response}` : ''}
                      {u.has_access ? ' · access ✓' : ' · no access'}
                    </div>
                  </div>
                  {!u.is_superuser && (
                    <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                      {/* Testing / Live / Comped */}
                      <div className="inline-flex rounded-lg border border-edge overflow-hidden text-[11px]"
                           role="group" aria-label="Billing phase">
                        {[['testing', 'Testing', 'bg-amber-300/15 text-amber-300',
                           'Testing — free and never charged until you move them to Live.'],
                          ['live', 'Live', 'bg-vblue/15 text-vblue',
                           'Live — real subscription. From Testing/Comped they must accept and pay, or stop, on next open.'],
                          ['comped', 'Comped', 'bg-emerald-400/15 text-emerald-400',
                           'Comped — free for good, never asked; any live subscription cancelled.'],
                        ].map(([k, label, on, tip], i) => (
                          <button key={k} type="button" onClick={() => setPhase(u, k)} title={tip}
                                  className={`px-2.5 py-1 transition ${i ? 'border-l border-edge' : ''} ${
                                    u.billing_phase === k ? `${on} font-semibold` : 'text-slate-400 hover:text-slate-200'}`}>
                            {label}
                          </button>
                        ))}
                      </div>
                      {/* Monthly / Yearly — only when a yearly plan is configured and the user is billed */}
                      {uYearly && u.billing_phase === 'live' && (
                        <div className="inline-flex rounded-lg border border-edge overflow-hidden text-[11px]"
                             role="group" aria-label="Plan interval">
                          <button type="button" onClick={() => setUserInterval(u, 'monthly')}
                                  title="Monthly plan (R59/mo). Active subs switch at next renewal — no charge now."
                                  className={`px-2.5 py-1 transition ${
                                    u.billing_interval !== 'yearly' ? 'bg-vblue/15 text-vblue font-semibold'
                                                                    : 'text-slate-400 hover:text-slate-200'}`}>Monthly</button>
                          <button type="button" onClick={() => setUserInterval(u, 'yearly')}
                                  title="Yearly plan (R590/yr). Active subs switch at next renewal — no charge now."
                                  className={`px-2.5 py-1 border-l border-edge transition ${
                                    u.billing_interval === 'yearly' ? 'bg-vblue/15 text-vblue font-semibold'
                                                                    : 'text-slate-400 hover:text-slate-200'}`}>Yearly</button>
                        </div>
                      )}
                      {/* Enable / disable the subscription — billed users only */}
                      {u.billing_phase === 'live' && (u.subscription_status === 'active' ? (
                        <button type="button" onClick={() => setUserSub(u, 'disable')}
                                title="Disable — cancel at Paystack; access lasts to the paid period end."
                                className="text-[11px] px-2.5 py-1 rounded-lg border border-amber-400/50 text-amber-400 hover:bg-amber-400/10 transition">Disable</button>
                      ) : (
                        <button type="button" onClick={() => setUserSub(u, 'enable')}
                                title="Enable — resume the cancelled subscription, or start one on the card on file."
                                className="text-[11px] px-2.5 py-1 rounded-lg border border-emerald-400/50 text-emerald-400 hover:bg-emerald-400/10 transition">Enable</button>
                      ))}
                      <button type="button" onClick={() => delUser(u)}
                              className="text-[11px] px-2.5 py-1 rounded-lg border border-red-400/50 text-red-400 hover:bg-red-400/10 transition">Delete</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>
        )}

        {tab === 'testers' && (
        <Section title="Tester list — who is free while testing (superuser)">
          {tListInfo && <div className="text-xs text-vblue mb-2">{tListInfo}</div>}
          <div className="text-[11px] text-slate-500 mb-3 leading-relaxed">
            Every account on this list is in <span className="text-amber-300 font-semibold">Testing</span> and is never charged.
            An email that has not signed up yet starts in Testing the moment it registers (email, Google or Apple).
            Removing an email from the list does not change that account; use the switch on the Users tab.
          </div>
          <textarea className="w-full bg-ink border border-edge rounded-lg px-3 py-2 text-sm" rows={3}
                    placeholder="Paste tester emails — commas, spaces, new lines or a copied BCC line all work"
                    value={tPaste} onChange={(e) => setTPaste(e.target.value)} />
          <div className="flex flex-wrap gap-2 mt-2 mb-3">
            <button onClick={addTesterEmails}
                    className="text-sm px-4 py-2 rounded-lg border border-vblue text-vblue hover:bg-vblue/10 transition">
              Add to tester list
            </button>
            <button onClick={() => bulkTesters('live')} disabled={!tList?.testing_count}
                    className="text-sm px-4 py-2 rounded-lg border border-amber-400/60 text-amber-300 hover:bg-amber-400/10 transition disabled:opacity-40">
              All testers → Live ({tList?.testing_count || 0})
            </button>
            <button onClick={() => bulkTesters('comped')} disabled={!tList?.testing_count}
                    className="text-sm px-4 py-2 rounded-lg border border-emerald-400/60 text-emerald-400 hover:bg-emerald-400/10 transition disabled:opacity-40">
              All testers → Comped ({tList?.testing_count || 0})
            </button>
          </div>
          {!tList ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : tList.emails.length === 0 ? (
            <div className="text-sm text-slate-500">No tester emails yet.</div>
          ) : (
            <div className="grid gap-1">
              {tList.emails.map((t) => (
                <div key={t.email} className="flex items-center gap-2 border-b border-edge last:border-0 py-1.5">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{t.email}</div>
                    <div className="text-[10px] text-slate-500">
                      {!t.user_id ? 'not signed up yet · will start in Testing'
                        : t.is_superuser ? 'owner'
                        : t.golive_pending ? 'live · waiting for their answer'
                        : `${t.billing_phase}${t.golive_response ? ` · go-live ${t.golive_response}` : ''}`}
                    </div>
                  </div>
                  <button onClick={() => removeTesterEmail(t.email)}
                          className="text-[11px] px-2.5 py-1 rounded-lg border border-edge text-slate-400 hover:text-slate-200 transition">
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </Section>
        )}

        {tab === 'testers' && (
        <Section title="Testers — email or WhatsApp them (superuser)">
          {tInfo && <div className="text-xs text-vblue mb-2">{tInfo}</div>}
          {!testers ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : testers.length === 0 ? (
            <div className="text-sm text-slate-500">No tester signups yet.</div>
          ) : (
            <>
              <div className="mb-3 grid gap-2">
                <div className="flex gap-2 flex-wrap">
                  {[['email', 'Email'], ['whatsapp', 'WhatsApp']].map(([k, l]) => (
                    <button key={k} onClick={() => setTMsg((m) => ({ ...m, channel: k }))}
                            className={`text-[11px] px-3 py-1 rounded-lg border transition ${
                              tMsg.channel === k ? 'border-vblue bg-vblue/10 text-vblue font-semibold'
                                                 : 'border-edge text-slate-400'}`}>{l}</button>
                  ))}
                  <button onClick={() => setTSel(testers.map((t) => t.id))}
                          className="text-[11px] px-3 py-1 rounded-lg border border-edge text-slate-300 ml-auto">Select all</button>
                  <button onClick={() => setTSel([])}
                          className="text-[11px] px-3 py-1 rounded-lg border border-edge text-slate-300">Clear</button>
                </div>
                {tMsg.channel === 'email' && (
                  <input className="w-full bg-ink border border-edge rounded-lg px-3 py-2 text-sm"
                         placeholder="Subject" value={tMsg.subject}
                         onChange={(e) => setTMsg((m) => ({ ...m, subject: e.target.value }))} />
                )}
                <textarea className="w-full bg-ink border border-edge rounded-lg px-3 py-2 text-sm" rows={4}
                          placeholder={tMsg.channel === 'whatsapp'
                            ? 'WhatsApp message — only delivers to people who messaged you in the last 24h unless you use an approved template'
                            : 'Email message to the selected testers'}
                          value={tMsg.body}
                          onChange={(e) => setTMsg((m) => ({ ...m, body: e.target.value }))} />
                <button onClick={sendToTesters}
                        className="text-sm px-4 py-2 rounded-lg border border-vblue text-vblue hover:bg-vblue/10 transition w-fit">
                  Send {tMsg.channel === 'whatsapp' ? 'WhatsApp' : 'email'} to {tSel.length} selected
                </button>
              </div>
              <div className="grid gap-1">
                {testers.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 border-b border-edge last:border-0 py-1.5 cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 accent-vblue"
                           checked={tSel.includes(t.id)} onChange={() => toggleSel(t.id)} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm truncate">{t.name || t.email}</div>
                      <div className="text-[10px] text-slate-500">
                        {t.email}{t.phone ? ` · ${t.phone}` : ''}{t.platform ? ` · ${t.platform}` : ''}
                        {t.bonus_days ? ` · +${t.bonus_days}d` : ''}{t.contacted_at ? ' · contacted' : ''}
                        {t.source === 'play' ? (t.play_added_at
                          ? ` · Play: added ${new Date(t.play_added_at).toLocaleDateString('en-ZA')}`
                          : ' · Play: waiting to be added') : ''}
                        {t.source === 'play' ? (t.has_account ? ' · has account' : ' · no account yet') : ''}
                      </div>
                    </div>
                    {t.source === 'play' && !t.play_added_at && (
                      <button type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); markPlayAdded(t); }}
                              className="text-[11px] px-3 py-1 rounded-lg border border-vblue text-vblue hover:bg-vblue/10 transition shrink-0">
                        Added to Play
                      </button>
                    )}
                  </label>
                ))}
              </div>
            </>
          )}
        </Section>
        )}
      </div>
    </div>
  );
}

/** The AI master switch (hub §5b), stated plainly at the top of the dashboard:
 * ON/OFF, how AI controls show while off, and this month's called vs skipped
 * calls. Flipping it applies within seconds on every surface, with no deploy. */
function AISwitchCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => {
    try { setS(await api('/ai/settings/')); } catch (e) { setMsg(e.data?.detail || e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async (body) => {
    if (body.ai_enabled === false
        // eslint-disable-next-line no-alert
        && !window.confirm('Switch ALL AI in Vault off? Every AI feature stops on web, phone, iPad and watches until you switch it back on.')) return;
    setBusy(true); setMsg('');
    try { setS(await api('/ai/settings/', { method: 'PATCH', body })); }
    catch (e) { setMsg(e.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  if (!s) return msg ? <div className="mb-4 text-xs text-red-400">{msg}</div> : null;
  const on = s.ai_enabled;
  return (
    <div className={`card mb-4 flex flex-wrap items-center gap-3 border ${on ? 'border-emerald-400/40' : 'border-amber-400/60'}`}>
      <div className="flex-1 min-w-[200px]">
        <div className="text-[10px] uppercase tracking-wide text-slate-500">AI master switch</div>
        <div className={`text-xl font-bold ${on ? 'text-emerald-400' : 'text-amber-300'}`}>AI is {on ? 'ON' : 'OFF'}</div>
        <div className="text-[11px] text-slate-500">
          This month: {s.month?.called ?? 0} AI calls · {s.month?.skipped ?? 0} skipped while off
        </div>
      </div>
      <div className="inline-flex rounded-lg border border-edge overflow-hidden text-sm" role="group" aria-label="AI on or off">
        <button type="button" disabled={busy} onClick={() => !on && save({ ai_enabled: true })}
                className={`px-4 py-1.5 ${on ? 'bg-emerald-400/15 text-emerald-400 font-semibold' : 'text-slate-400 hover:text-slate-200'}`}>On</button>
        <button type="button" disabled={busy} onClick={() => on && save({ ai_enabled: false })}
                className={`px-4 py-1.5 border-l border-edge ${!on ? 'bg-amber-400/15 text-amber-300 font-semibold' : 'text-slate-400 hover:text-slate-200'}`}>Off</button>
      </div>
      <label className="text-[11px] text-slate-400 flex items-center gap-2">
        While off, AI controls are
        <select className="bg-ink border border-edge rounded-lg px-2 py-1 text-xs" disabled={busy}
                value={s.ai_off_display} onChange={(e) => save({ ai_off_display: e.target.value })}>
          <option value="disabled_request">shown disabled, with "Ask about enabling AI"</option>
          <option value="hidden">hidden</option>
        </select>
      </label>
      {msg && <div className="w-full text-xs text-red-400">{msg}</div>}
    </div>
  );
}

function Hero({ label, value, sub, accent }) {
  const c = accent === 'emerald' ? 'text-emerald-400' : 'text-vblue';
  return (
    <div className="card">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-2xl font-bold ${c}`}>{fmtR(value)}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}
function Stat({ label, value, neg }) {
  return (
    <div className="border border-edge rounded-xl px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 truncate">{label}</div>
      <div className={`text-sm font-semibold ${neg ? 'text-slate-300' : ''}`}>{fmtR(value)}</div>
    </div>
  );
}
function Section({ title, action, children }) {
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1.5">
        <h2 className="text-xs font-bold uppercase tracking-wide text-vblue"
            dangerouslySetInnerHTML={{ __html: title }} />
        {action}
      </div>
      <div className="card">{children}</div>
    </div>
  );
}
function Pill({ k, v }) {
  return (
    <span className="px-2.5 py-1 rounded-lg border border-edge">
      <span className="text-slate-400">{k} </span><span className="font-semibold">{v}</span>
    </span>
  );
}
function Row({ title, amount, sub }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-edge last:border-0">
      <div className="min-w-0">
        <div className="text-sm truncate">{title}</div>
        {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
      </div>
      <div className="text-sm font-semibold shrink-0">{fmtR(amount)}</div>
    </div>
  );
}
